import React, { useState, useRef } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Text } from '@app/design-system';
import { useWebsiteBuilder } from '../../store/websiteBuilderStore';
import { BlockRenderer } from './BlockRenderer';

export const CenterCanvas: React.FC = () => {
  const { config, viewport, zoom, selectedBlockId, setSelectedBlock, reorderBlocks } = useWebsiteBuilder();
  const [draggedBlockIndex, setDraggedBlockIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef<number>(0);
  const scrollViewRef = useRef<any>(null);

  // Use theme from website builder config, not from app theme
  const themeColors = {
    primary: config?.theme?.primaryColor || '#34C759',
    secondary: config?.theme?.secondaryColor || '#F5F5F5',
    text: config?.theme?.textColor || '#1D1D1F',
    background: config?.theme?.backgroundColor || '#FFFFFF',
  };
  const accentColor = config?.theme?.accentColor || '#FF9500';

  const handleDragStart = (index: number, event?: any) => {
    console.log('🎯 [Drag] Started dragging block at index:', index);
    setDraggedBlockIndex(index);
    setIsDragging(true);
    if (event?.nativeEvent) {
      dragStartY.current = event.nativeEvent.pageY || event.nativeEvent.clientY || 0;
    }
  };

  const handleDragOver = (index: number) => {
    if (draggedBlockIndex !== null && draggedBlockIndex !== index) {
      setDropTargetIndex(index);
    }
  };

  const handleDrop = (targetIndex: number) => {
    console.log('📥 [Drop] Dropping block at index:', targetIndex, 'from:', draggedBlockIndex);
    if (draggedBlockIndex !== null && draggedBlockIndex !== targetIndex) {
      // Adjust target index if dragging down
      const actualTargetIndex = draggedBlockIndex < targetIndex ? targetIndex - 1 : targetIndex;
      console.log('🔄 [Reorder] Moving from', draggedBlockIndex, 'to', actualTargetIndex);
      reorderBlocks(draggedBlockIndex, actualTargetIndex);
    }
    handleDragEnd();
  };

  const handleDragEnd = () => {
    console.log('🏁 [Drag] Drag ended');
    setDraggedBlockIndex(null);
    setDropTargetIndex(null);
    setIsDragging(false);
    dragStartY.current = 0;
  };

  const moveBlockUp = (index: number) => {
    if (index > 0) {
      console.log('⬆️ [Move] Moving block up from index:', index);
      reorderBlocks(index, index - 1);
    }
  };

  const moveBlockDown = (index: number) => {
    if (config && index < config.blocks.length - 1) {
      console.log('⬇️ [Move] Moving block down from index:', index);
      reorderBlocks(index, index + 1);
    }
  };

  const viewportWidths = {
    mobile: 375,
    tablet: 768,
    desktop: 1200,
  };

  const canvasWidth = viewportWidths[viewport];
  const scale = zoom / 100;

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#F5F5F5',
      alignItems: 'center',
    },
    scrollContainer: {
      flex: 1,
      width: '100%',
    },
    scrollContent: {
      alignItems: 'center',
      paddingVertical: 32,
      paddingHorizontal: 16,
      minHeight: '100%',
    },
    canvasFrame: {
      width: canvasWidth,
      transform: [{ scale }],
      backgroundColor: themeColors.background,
      borderRadius: 8,
      overflow: 'visible', // Changed to visible for drag handles
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 12,
      elevation: 4,
      paddingLeft: 48, // Space for drag controls
    },
    blockWrapper: {
      position: 'relative',
      marginVertical: 4,
    },
    blockInner: {
      position: 'relative',
    },
    dragging: {
      opacity: 0.4,
      backgroundColor: themeColors.secondary,
    },
    dropZone: {
      height: 40,
      backgroundColor: accentColor + '20',
      borderWidth: 2,
      borderColor: accentColor,
      borderStyle: 'dashed',
      borderRadius: 4,
      marginVertical: 4,
      justifyContent: 'center',
      alignItems: 'center',
    },
    dropZoneHidden: {
      height: 0,
      marginVertical: 0,
    },
    dropZoneText: {
      color: accentColor,
      fontSize: 12,
      fontWeight: '600',
    },
    controlsContainer: {
      position: 'absolute',
      left: -44,
      top: 0,
      bottom: 0,
      width: 40,
      justifyContent: 'center',
      alignItems: 'center',
      gap: 4,
      zIndex: 10,
    },
    dragHandle: {
      width: 32,
      height: 32,
      backgroundColor: themeColors.primary,
      borderRadius: 6,
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'grab',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.2,
      shadowRadius: 4,
      elevation: 3,
    },
    dragHandleActive: {
      cursor: 'grabbing',
      backgroundColor: accentColor,
    },
    dragHandleIcon: {
      color: '#FFFFFF',
      fontSize: 18,
      fontWeight: '700',
    },
    moveButton: {
      width: 24,
      height: 24,
      backgroundColor: themeColors.secondary,
      borderWidth: 1,
      borderColor: themeColors.primary + '40',
      borderRadius: 4,
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
    },
    moveButtonDisabled: {
      opacity: 0.3,
      cursor: 'not-allowed',
    },
    moveButtonIcon: {
      color: themeColors.primary,
      fontSize: 12,
      fontWeight: '700',
    },
    emptyState: {
      padding: 64,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 400,
    },
    emptyIcon: {
      fontSize: 64,
      marginBottom: 16,
    },
    emptyTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: themeColors.text || "#000000",
      marginBottom: 8,
    },
    emptyText: {
      fontSize: 14,
      color: themeColors.text || "#666666",
      textAlign: 'center',
      maxWidth: 300,
    },
  });

  return (
    <View style={styles.container}>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.canvasFrame}>
          {!config || !config.blocks || config.blocks.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyIcon}>🎨</Text>
              <Text style={styles.emptyTitle}>Start Building Your Website</Text>
              <Text style={styles.emptyText}>
                Add blocks from the left panel or use the template options
              </Text>
            </View>
          ) : (
            <>
              {config.blocks
                .sort((a, b) => a.order - b.order)
                .map((block, index) => (
                  <React.Fragment key={block.id}>
                    {/* Drop zone at the top of first block */}
                    {index === 0 && dropTargetIndex === 0 && draggedBlockIndex !== null && (
                      <View style={styles.dropZone}>
                        <Text style={styles.dropZoneText}>Drop here</Text>
                      </View>
                    )}

                    {/* Block with drag controls */}
                    <View
                      style={[
                        styles.blockWrapper,
                        draggedBlockIndex === index && styles.dragging,
                      ]}
                    >
                      {/* Drag and Move Controls */}
                      <View style={styles.controlsContainer}>
                        {/* Move Up Button */}
                        <TouchableOpacity
                          style={[
                            styles.moveButton,
                            index === 0 && styles.moveButtonDisabled,
                          ]}
                          onPress={() => moveBlockUp(index)}
                          disabled={index === 0}
                        >
                          <Text style={styles.moveButtonIcon}>▲</Text>
                        </TouchableOpacity>

                        {/* Drag Handle */}
                        <TouchableOpacity
                          style={[
                            styles.dragHandle,
                            draggedBlockIndex === index && styles.dragHandleActive,
                          ]}
                          onPressIn={(e) => handleDragStart(index, e)}
                          onLongPress={() => handleDragStart(index)}
                          delayLongPress={100}
                        >
                          <Text style={styles.dragHandleIcon}>⋮⋮</Text>
                        </TouchableOpacity>

                        {/* Move Down Button */}
                        <TouchableOpacity
                          style={[
                            styles.moveButton,
                            index === config.blocks.length - 1 && styles.moveButtonDisabled,
                          ]}
                          onPress={() => moveBlockDown(index)}
                          disabled={index === config.blocks.length - 1}
                        >
                          <Text style={styles.moveButtonIcon}>▼</Text>
                        </TouchableOpacity>
                      </View>

                      {/* Block Content */}
                      <View
                        style={styles.blockInner}
                        {...(Platform.OS === 'web' && {
                          draggable: true,
                          onDragStart: () => handleDragStart(index),
                          onDragEnd: handleDragEnd,
                          onDragOver: (e: any) => {
                            e.preventDefault();
                            handleDragOver(index);
                          },
                          onDrop: () => handleDrop(index),
                        })}
                      >
                        <BlockRenderer
                          block={block}
                          isSelected={selectedBlockId === block.id}
                          onSelect={() => setSelectedBlock(block.id)}
                        />
                      </View>
                    </View>

                    {/* Drop zone between blocks and at the end */}
                    {dropTargetIndex === index + 1 && draggedBlockIndex !== null && (
                      <View style={styles.dropZone}>
                        <Text style={styles.dropZoneText}>Drop here</Text>
                      </View>
                    )}
                  </React.Fragment>
                ))}
            </>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

